import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { ICollectionProducts } from "../../interfaces/shopify";
import { getCollections } from "../../services/getCollections";
import {
  Box,
  CardMedia,
  Fade,
  Grid,
  LinearProgress,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { CardProduct } from "../../components/CardProduct";
import { LoadPage } from "../../components/LoadPage";

export const Collection = () => {
  const theme = useTheme();
  const { name } = useParams();
  const [collection, setCollection] = useState<ICollectionProducts>();
  const [loading, setLoading] = useState(false);
  const [cursor, setCursor] = useState<string | null>(null);
  const loaderRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setLoading(true);
    const getData = async () => {
      try {
        if (!name) return;
        const data = await getCollections(name, cursor ?? undefined);
        setCollection((prev) => {
          if (cursor && prev) {
            return {
              ...data,
              products: [...prev.products, ...data.products],
            };
          }
          return data;
        });
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };
    getData();
  }, [name, cursor]);

  const loadMore = useCallback(() => {
    if (collection?.pageInfo?.hasNextPage) {
      setCursor(collection.pageInfo.endCursor);
    }
  }, [collection]);

  useEffect(() => {
    if (!loaderRef.current) return;

    const target = loaderRef.current;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loading) {
          loadMore();
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(target);

    return () => {
      observer.unobserve(target);
    };
  }, [loading, loadMore]);

  return (
    <React.Fragment>
      {loading && !collection ? (
        <LoadPage />
      ) : (
        <Fade in timeout={1000}>
          <Box>
            <Grid
              spacing={{ xs: 2, md: 3 }}
              container
              mt={5}
              marginInline={{ xs: 2, md: 5 }}
            >
              <Grid size={12}>
                {collection?.banner?.references.edges.map((b) => (
                  <Box
                    overflow="hidden"
                    mb={8}
                    key={b.node.id}
                    position="relative"
                  >
                    <Box
                      sx={{
                        zIndex: 2,
                        width: "100%",
                        position: "relative",
                        backgroundColor: "rgba(255, 255, 255, 0.90)",
                        backdropFilter: "saturate(200%) blur(80px)",
                        borderRadius: 2,
                        height: { xs: 250, md: 450 },
                      }}
                    >
                      <CardMedia
                        loading="lazy"
                        sx={{
                          position: "absolute",
                          right: { xs: 15, md: 40 },
                          bottom: { xs: 0, md: 0 },
                          width: {xs: 200, md: "auto",},
                          height: { xs: 250, md: 450 },
                        }}
                        component="img"
                        image={b.node.fields[0].reference?.image.url}
                        alt={b.node.fields[0].reference?.image.altText ?? ""}
                      />

                      <Box
                        position="absolute"
                        bottom={{ xs: 15, md: 40 }}
                        left={{ xs: 15, md: 40 }}
                      >
                        <Typography
                          maxWidth={700}
                          fontWeight={700}
                          fontSize={{ xs: 13, md: 25 }}
                          sx={{ color: theme.palette.secondary[500] }}
                        >
                          {b.node.fields[2].value}
                        </Typography>
                        <Typography
                          maxWidth={700}
                          fontWeight={700}
                          fontSize={{ xs: 16, md: 35 }}
                          sx={{ color: theme.palette.secondary[500] }}
                        >
                          {b.node.fields[1].value}
                        </Typography>
                      </Box>
                    </Box>
                    <video
                      width="100%"
                      height="100%"
                      style={{
                        left: 0,
                        top: 0,
                        objectFit: "cover",
                        borderRadius: 60,
                        position: "absolute",
                        width: "100%",
                        display: "block",
                        zIndex: 1,
                      }}
                      muted
                      loop
                      playsInline
                      autoPlay
                      poster="https://thtlajzibybayogrvjsd.supabase.co/storage/v1/object/public/nikolay/videos/transitions/poster.jpg"
                      src="https://thtlajzibybayogrvjsd.supabase.co/storage/v1/object/public/nikolay/videos/transitions/video.mp4"
                    ></video>
                  </Box>
                ))}
              </Grid>
              {collection?.products
                .filter(
                  (p) =>
                    p?.images?.edges?.length &&
                    p.images.edges[0]?.node &&
                    p?.variants?.edges?.length &&
                    p.variants.edges[0]?.node
                )
                .map((p) => (
                  <Fade in={true} timeout={1500} key={`${p.id}-${p.handle}`}>
                    <Grid size={{ xs: 6, sm: 4, md: 3, lg: 2 }}>
                      <CardProduct
                        to={`/products/${p.handle}`}
                        image={p.images.edges[0].node.url}
                        alt={p.images.edges[0].node.altText ?? ""}
                        title={p.title}
                        category={collection?.categoryTitle}
                        currency={p.variants.edges[0].node.price.currencyCode}
                        price={p.variants.edges[0].node.price.amount}
                        comparePrice={p.variants.edges[0].node.compareAtPrice?.amount}
                      />
                    </Grid>
                  </Fade>
                ))}
            </Grid>

            <Box ref={loaderRef} sx={{ width: "100%", height: 100 }} />
            {loading && (
              <Stack
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  flexDirection: "column",
                  gap: 1,
                  alignItems: "center",
                }}
              >
                <LinearProgress
                  sx={{ width: { xs: 90, sm: 120, md: 170 }, borderRadius: 5 }}
                  color="primary"
                />
              </Stack>
            )}
          </Box>
        </Fade>
      )}
    </React.Fragment>
  );
};
